import { z } from 'zod';

import { ZodAmount } from '@Common/types/Amount';
import { ZodTaigaSavingPlanId, ZodTaigaSavingPlanIdPathParam } from '@Common/types/b2b/axa/plans';
import {
  B2bPlanTypeEr,
  B2bPlanTypeErExternal,
  B2bPlanTypeErExternalValues,
  B2bPlanTypeErValues,
  B2bPlanTypeEsLibre,
  B2bPlanTypeEsLibreValues,
  B2bPlanTypeEsPilote,
  B2bPlanTypeEsPiloteValues,
  ZodB2bSavingPlanCategory,
  ZodB2bSavingPlanType,
} from '@Common/types/b2b/plans';
import { ZodB2bCashSource } from '@Common/types/bo/b2b/b2b';
import { ZodId } from '@Common/types/request';
import { ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const ZodInternalSavingPlanId = z
  .string()
  .regex(/^IA_\d+_SP_\d+$/, 'Expected format: IA_number_SP_number');

export type InternalSavingPlanId = z.TypeOf<typeof ZodInternalSavingPlanId>;

export const ZodTransformedInternalSavingPlanId = ZodInternalSavingPlanId.transform((val) => {
  const match = val.match(/^IA_(\d+)_SP_(\d+)$/);
  if (!match) {
    throw new Error('Invalid InvestmentAccountSavingPlanId format');
  }

  const [, investmentAccountId, savingPlanId] = match;

  return {
    investment_account_id: parseInt(investmentAccountId, 10),
    saving_plan_id: parseInt(savingPlanId, 10),
  };
}).meta({ override: { type: 'string' } });

export const ZodTransformedProfileSavingPlanId = ZodTaigaSavingPlanId.or(
  ZodTransformedInternalSavingPlanId,
);

export type TransformedProfileSavingPlanId = z.TypeOf<typeof ZodTransformedProfileSavingPlanId>;

export const ZodProfileSavingPlanId = ZodTaigaSavingPlanId.or(ZodInternalSavingPlanId);
export type ProfileSavingPlanId = z.TypeOf<typeof ZodProfileSavingPlanId>;

export const ZodProfileSavingPlanIdUriSafe =
  ZodTaigaSavingPlanIdPathParam.or(ZodInternalSavingPlanId);
export type ProfileSavingPlanIdUriSafe = z.TypeOf<typeof ZodProfileSavingPlanIdUriSafe>;

const b2bEmployeeEmploymentStatusValues = ['active', 'inactive'] as const;

export type B2bEmployeeEmploymentStatus = (typeof b2bEmployeeEmploymentStatusValues)[number];

export const ZodProfileSavingPlan = z.object({
  id: ZodProfileSavingPlanId,
  company_id: z.number().int(),
  name: ZodTrimmedString,
  label: ZodTrimmedString.nullable(),
  type: ZodB2bSavingPlanType,
  default_fund_instrument_id: z.number().int().nullable(),
  has_invested: z.boolean(),
  employment_status: z.enum(b2bEmployeeEmploymentStatusValues),
  cash_sources: ZodB2bCashSource.array(),
  managed_external_company_name: ZodTrimmedString.nullable(),
});

export type ProfileSavingPlan = z.TypeOf<typeof ZodProfileSavingPlan>;

export const ZodProfileSavingPlansResponse = z.object({
  plans: ZodProfileSavingPlan.refine(
    ({ type, default_fund_instrument_id }) =>
      (B2bPlanTypeEsLibreValues.includes(type as B2bPlanTypeEsLibre) &&
        default_fund_instrument_id !== null) ||
      ([
        ...B2bPlanTypeEsPiloteValues,
        ...B2bPlanTypeErValues,
        ...B2bPlanTypeErExternalValues,
      ].includes(type as B2bPlanTypeEsPilote | B2bPlanTypeEr | B2bPlanTypeErExternal) &&
        default_fund_instrument_id === null),
    {
      message:
        'the default_fund_instrument_id must be set if type is LIBRE and must not be set otherwise',
    },
  ).array(),
});

export const getProfileSavingPlansEndpoint = endpoint({
  method: 'GET',
  responseBody: ZodProfileSavingPlansResponse,
});

export type GetProfileSavingPlansEndpoint = GetEndpointType<typeof getProfileSavingPlansEndpoint>;

export const ZodRetirement = z.literal('retirement');

const ZodFundData = z.discriminatedUnion('lock_type', [
  z.object({
    date: ZodDateTimeString.optional(),
    lock_type: ZodRetirement,
    amount: ZodAmount,
  }),
  z.object({
    date: ZodDateTimeString,
    lock_type: z.literal('period'),
    amount: ZodAmount,
  }),
]);

export const ZodPositionData = z.object({
  instrument_id: z.number().int(),
  amount: ZodAmount,
});

export type FundDataType = z.TypeOf<typeof ZodFundData>;

export type PositionData = z.infer<typeof ZodPositionData>;

const ZodProfileSavingPlanLockedFunds = z.object({
  plan_id: ZodProfileSavingPlanId,
  type: ZodB2bSavingPlanType,
  category: ZodB2bSavingPlanCategory,
  minimum_period_years: z.union([z.number().int(), ZodRetirement]),
  account_open: ZodDateTimeString.optional(),
  withdrawable: ZodAmount,
  total: ZodAmount,
  withdrawable_positions: z.array(ZodPositionData),
  locked_positions: z.array(ZodPositionData),
  locked_funds: z.array(ZodFundData),
  withdrawable_funds: z.array(ZodFundData),
  restricted_instrument_ids: z.number().int().array(),
});

export type ProfileSavingPlanLockedFunds = z.TypeOf<typeof ZodProfileSavingPlanLockedFunds>;

const ZodProfileSavingPlanCategoryLockedFunds = z.object({
  ...ZodProfileSavingPlanLockedFunds.omit({
    plan_id: true,
    category: true,
    type: true,
    account_open: true,
    restricted_instrument_ids: true,
  }).shape,
  type: ZodB2bSavingPlanCategory,
});

const ZodProfileCompanySavingPlanCategoryLockedFunds = z.object({
  ...ZodProfileSavingPlanCategoryLockedFunds.shape,
  restricted_instrument_ids: z.number().int().array(),
});

export type ProfileCompanySavingPlanCategoryLockedFunds = z.TypeOf<
  typeof ZodProfileCompanySavingPlanCategoryLockedFunds
>;

export const ZodProfileCompanySavingsLockedFunds = z.object({
  account_open: ZodDateTimeString.optional(),
  plans: z.array(ZodProfileSavingPlanLockedFunds),
  categories: z.array(ZodProfileCompanySavingPlanCategoryLockedFunds),
  withdrawable: ZodAmount,
  total: ZodAmount,
});

export const ZodProfileSavingsLockedFunds = z.object({
  categories: z.array(ZodProfileSavingPlanCategoryLockedFunds),
  withdrawable: ZodAmount,
  total: ZodAmount,
  per_company: z.array(
    z.object({
      ...ZodProfileCompanySavingsLockedFunds.shape,
      company_id: z.number().int(),
    }),
  ),
});

export type ProfileCompanySavingsLockedFunds = z.TypeOf<typeof ZodProfileCompanySavingsLockedFunds>;
export type ProfileSavingsLockedFunds = z.TypeOf<typeof ZodProfileSavingsLockedFunds>;

export const getProfileSavingPlansLockedFundsEndpoint = endpoint({
  method: 'GET',
  queryParams: createZodParamsValidator(
    z.object({
      company_id: ZodId.optional(),
    }),
  ),
  responseBody: ZodProfileSavingsLockedFunds,
});

export type GetProfileSavingPlansLockedFundsEndpoint = GetEndpointType<
  typeof getProfileSavingPlansLockedFundsEndpoint
>;

export const ZodWithdrawalPerspectiveAvailability = z.enum([
  'available_now',
  'within_5_years',
  'from_retirement',
]);

export type WithdrawalPerspectiveAvailability = z.infer<
  typeof ZodWithdrawalPerspectiveAvailability
>;

export const ZodWithdrawalPerspectivePlanCategory = ZodB2bSavingPlanCategory.or(z.literal('BCAC'));

export type WithdrawalPerspectivePlanCategory = z.infer<
  typeof ZodWithdrawalPerspectivePlanCategory
>;

export const ZodWithdrawalPerspectiveFund = z.object({
  instrument_id: z.number().int(),
  company_id: z.number().int(),
  plan_id: ZodProfileSavingPlanId,
  plan_category: ZodB2bSavingPlanCategory,
  amount: ZodAmount,
  allocation_percentage: z.number().min(0).max(100),
  account_open: ZodDateTimeString.optional(),
  minimum_period_years: z.union([z.number().int(), ZodRetirement]),
  retirement_date: ZodDateTimeString.optional(),
  withdrawal_date: ZodDateTimeString,
});

export type WithdrawalPerspectiveFund = z.infer<typeof ZodWithdrawalPerspectiveFund>;

const ZodWithdrawalPerspectiveSection = z.object({
  total: ZodAmount,
  percentage: z.number().min(0).max(100),
  funds: z.array(ZodWithdrawalPerspectiveFund),
});

export type WithdrawalPerspectiveSection = z.infer<typeof ZodWithdrawalPerspectiveSection>;

export const ZodProfileSavingsWithdrawalPerspective = z.object({
  total: ZodAmount,
  available_now: ZodWithdrawalPerspectiveSection,
  within_5_years: ZodWithdrawalPerspectiveSection,
  from_retirement: ZodWithdrawalPerspectiveSection,
});

export type ProfileSavingsWithdrawalPerspective = z.infer<
  typeof ZodProfileSavingsWithdrawalPerspective
>;

export const getProfileSavingPlansWithdrawalPerspectiveEndpoint = endpoint({
  method: 'GET',
  queryParams: createZodParamsValidator(
    z.object({
      company_id: ZodId.array().optional(),
      plan_category: ZodWithdrawalPerspectivePlanCategory.array().optional(),
    }),
  ),
  responseBody: ZodProfileSavingsWithdrawalPerspective,
});

export type GetProfileSavingPlansWithdrawalPerspectiveEndpoint = GetEndpointType<
  typeof getProfileSavingPlansWithdrawalPerspectiveEndpoint
>;

const ZodProfileSavingsAmountTotals = z.object({
  locked: ZodAmount,
  withdrawable: ZodAmount,
  total: ZodAmount,
});

const ZodProfileSavingsCategoryAmounts = z.object({
  type: ZodB2bSavingPlanCategory,
  ...ZodProfileSavingsAmountTotals.shape,
  unrestricted: ZodProfileSavingsAmountTotals,
});

const ZodProfileSavingsAmountsGroup = z.object({
  ...ZodProfileSavingsAmountTotals.shape,
  unrestricted: ZodProfileSavingsAmountTotals,
  categories: z.array(ZodProfileSavingsCategoryAmounts),
});

const ZodProfileCompanySavingsAmounts = z.object({
  company_id: z.number().int(),
  ...ZodProfileSavingsAmountTotals.shape,
  unrestricted: ZodProfileSavingsAmountTotals,
  es: ZodProfileSavingsAmountsGroup,
  taiga: ZodProfileSavingsAmountsGroup,
});

export const ZodProfileSavingsAmounts = z.object({
  ...ZodProfileSavingsAmountTotals.shape,
  unrestricted: ZodProfileSavingsAmountTotals,
  es: ZodProfileSavingsAmountsGroup,
  taiga: ZodProfileSavingsAmountsGroup,
  per_company: z.array(ZodProfileCompanySavingsAmounts),
});

export type ProfileSavingsAmounts = z.TypeOf<typeof ZodProfileSavingsAmounts>;
export type ProfileSavingsAmountsGroup = z.TypeOf<typeof ZodProfileSavingsAmountsGroup>;
export type ProfileCompanySavingsAmounts = z.TypeOf<typeof ZodProfileCompanySavingsAmounts>;
export type ProfileSavingsCategoryAmounts = z.TypeOf<typeof ZodProfileSavingsCategoryAmounts>;
export type ProfileSavingsAmountTotals = z.TypeOf<typeof ZodProfileSavingsAmountTotals>;

export const getProfileSavingPlansAmountsEndpoint = endpoint({
  method: 'GET',
  queryParams: createZodParamsValidator(
    z.object({
      company_id: ZodId.optional(),
      excluded_plan_ids: ZodProfileSavingPlanId.array().optional(),
    }),
  ),
  responseBody: ZodProfileSavingsAmounts,
});

export type GetProfileSavingPlansAmountsEndpoint = GetEndpointType<
  typeof getProfileSavingPlansAmountsEndpoint
>;

const ZodB2BSavingPlanDocumentType = z.enum([
  'employee_savings_booklet',
  'general_terms_and_conditions',
  'plan_regulations',
  'price_list_employee',
  'price_list_company',
]);

export type B2bSavingPlanDocumentType = z.TypeOf<typeof ZodB2BSavingPlanDocumentType>;

export const getProfileSavingPlansDocumentsEndpoint = endpoint({
  method: 'GET',
  queryParams: createZodParamsValidator(
    z.object({
      saving_plan_id: ZodProfileSavingPlanId,
    }),
  ),
  responseBody: z.object({
    documents: z.array(
      z.object({
        type: ZodB2BSavingPlanDocumentType,
        document_id: ZodId,
      }),
    ),
  }),
});

export type GetProfileSavingPlansDocumentsEndpoint = GetEndpointType<
  typeof getProfileSavingPlansDocumentsEndpoint
>;

export const getProfileSavingPlansDocumentUrlEndpoint = endpoint({
  method: 'GET',
  queryParams: createZodParamsValidator(
    z.object({
      document_id: ZodId,
    }),
  ),
  responseBody: z.object({
    url: z.url(),
  }),
});

export type GetProfileSavingPlansDocumentUrlEndpoint = GetEndpointType<
  typeof getProfileSavingPlansDocumentUrlEndpoint
>;
